import { useEffect, useRef, useState } from 'react'

import Input from '@shared/ui/input'

import styles from './AutocompleteInput.module.scss'

interface AutocompleteInputProps {
  placeholder: string
  value: string
  onChange: (value: string) => void
  onBlur: () => void
  onFocus?: () => void
  isInvalid?: boolean
  disabled?: boolean
  theme?: 'grey'
  inputSize?: 'extra_large'
  suggestions: string[]
  onInputChange: (value: string) => void
}

export default function AutocompleteInput ({
  placeholder,
  value,
  onChange,
  onBlur,
  onFocus,
  isInvalid,
  disabled,
  theme,
  inputSize,
  suggestions,
  onInputChange,
}: AutocompleteInputProps) {
  const [ showSuggestions, setShowSuggestions ] = useState(false)
  const [ highlightedIndex, setHighlightedIndex ] = useState(-1)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setShowSuggestions(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  useEffect(() => {
    if (suggestions.length > 0) {
      setShowSuggestions(true)
      setHighlightedIndex(-1)
    }
    else {
      setShowSuggestions(false)
    }
  }, [ suggestions ])

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value
    onChange(newValue)
    onInputChange(newValue)
  }

  const handleSuggestionClick = (suggestion: string) => {
    onChange(suggestion)
    setShowSuggestions(false)
    setHighlightedIndex(-1)
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showSuggestions || suggestions.length === 0) return

    switch (e.key) {
    case 'ArrowDown':
      e.preventDefault()
      setHighlightedIndex((prev) => 
        prev < suggestions.length - 1 ? prev + 1 : prev
      )
      break
    case 'ArrowUp':
      e.preventDefault()
      setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : 0))
      break
    case 'Enter':
      e.preventDefault()
      if (highlightedIndex >= 0 && highlightedIndex < suggestions.length) {
        handleSuggestionClick(suggestions[highlightedIndex])
      }
      break
    case 'Escape':
      setShowSuggestions(false)
      setHighlightedIndex(-1)
      break
    }
  }

  return (
    <div ref={containerRef} className={styles.container}>
      <Input
        placeholder={placeholder}
        value={value}
        onChange={handleInputChange}
        onBlur={onBlur}
        onFocus={() => {
          onFocus?.()
          if (suggestions.length > 0) {
            setShowSuggestions(true)
          }
        }}
        onKeyDown={handleKeyDown}
        isInvalid={isInvalid}
        disabled={disabled}
        theme={theme}
        inputSize={inputSize}
      />
      {showSuggestions && suggestions.length > 0 && (
        <ul className={styles.suggestions}>
          {suggestions.map((suggestion, index) => (
            <li
              key={index}
              className={`${styles.suggestion} ${
                index === highlightedIndex ? styles.highlighted : ''
              }`}
              onMouseDown={(e) => {
                e.preventDefault() // Prevent onBlur from firing
                handleSuggestionClick(suggestion)
              }}
              onMouseEnter={() => setHighlightedIndex(index)}
            >
              {suggestion}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
